<template>
  <v-dialog v-model="dialog" max-width="780" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        Nuevo precio por modelo
        <v-spacer />
        <v-btn icon @click="dialog = false"><v-icon>fas fa-times</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-row dense class="mt-2">
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-autocomplete
              v-model="data.empresa"
              label="Empresa"
              :items="familias"
              item-text="nombre"
              return-object
              hide-details
              outlined
              dense
              clearable
              @change="onEmpresaChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-autocomplete
              v-model="data.marca"
              label="Marca"
              :items="marcasOpciones"
              item-text="nombre"
              item-value="id"
              hide-details
              outlined
              dense
              clearable
              @change="onMarcaChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-autocomplete
              v-model="data.modelo"
              label="Modelo"
              :items="modelosOpciones"
              item-text="nombre"
              item-value="Modelo_id"
              :disabled="!data.marca"
              hide-details
              outlined
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <FechaPicker v-model="data.desde" label="Fecha desde" hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <FechaPicker v-model="data.hasta" label="Fecha hasta" hide-details clearable />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <TextFieldMoney
              v-model="data.precio"
              type="number"
              label="Precio"
              :properties="{ prefix: '$', outlined: true, dense: true }"
            />
          </v-col>
        </v-row>
        <div class="d-flex justify-end mt-4">
          <v-btn color="secondary" class="mr-2" @click="dialog = false">Volver</v-btn>
          <BtnConfirmar @action="guardarPrecio" />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import FechaPicker from '../util/FechaPicker.vue'
import BtnConfirmar from '../util/BtnConfirmar.vue'
import TextFieldMoney from '../util/TextFieldMoney.vue'

export default {
  name: 'NuevoPrecioModeloV2',
  components: { FechaPicker, BtnConfirmar, TextFieldMoney },
  props: {
    value: { type: Boolean, default: false },
    precios: { type: Array, default: () => [] }
  },
  data() {
    return {
      data: {
        empresa: null,
        marca: null,
        modelo: null,
        desde: moment().format('DD/MM/YYYY'),
        hasta: '',
        precio: null
      },
      marcasOpciones: [],
      modelosPorFamilia: [],
      modelosOpciones: []
    }
  },
  computed: {
    dialog: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    ...mapState(['familias', 'empresas'])
  },
  watch: {
    value(open) {
      if (open) {
        this.data = {
          empresa: null,
          marca: null,
          modelo: null,
          desde: moment().format('DD/MM/YYYY'),
          hasta: '',
          precio: null
        }
        this.marcasOpciones = []
        this.modelosPorFamilia = []
        this.modelosOpciones = []
        if (this.familias && this.familias.length === 1) {
          this.data.empresa = this.familias[0]
          this.onEmpresaChange()
        }
      }
    }
  },
  methods: {
    async onEmpresaChange() {
      this.data.marca = null
      this.data.modelo = null
      this.marcasOpciones = []
      this.modelosPorFamilia = []
      this.modelosOpciones = []
      if (!this.data.empresa || !this.data.empresa.id) return
      this.marcasOpciones = (this.empresas || []).filter(e => e.familia === this.data.empresa.id)
      if (this.marcasOpciones.length === 1) this.data.marca = this.marcasOpciones[0].id
      const res = await this.$store.dispatch('preciosModelosV2/getModelosByFamilia', this.data.empresa.id)
      if (res.exito === 1 && res.data) {
        this.modelosPorFamilia = res.data
        this.onMarcaChange()
      }
    },
    onMarcaChange() {
      this.data.modelo = null
      if (!this.data.marca) {
        this.modelosOpciones = []
        return
      }
      this.modelosOpciones = this.modelosPorFamilia
        .filter(m => !m.Inhabilitado && m.Empresa_id === this.data.marca)
        .map(m => ({ ...m, nombre: `${m.Descripcion} - ${m.Modelo}` }))
    },
    validarSolapamiento() {
      const preciosDelModelo = (this.precios || []).filter(p => p.modelo_id === this.data.modelo || p.Modelo_id === this.data.modelo)
      if (preciosDelModelo.length === 0) return true
      const d = moment(this.data.desde, 'DD/MM/YYYY')
      const h = this.data.hasta ? moment(this.data.hasta, 'DD/MM/YYYY') : null
      if (!d.isValid()) return true
      const solapa = preciosDelModelo.some(p => {
        const pd = moment(p.fecha_desde_fmt || p.fecha_desde, 'DD/MM/YYYY')
        const ph = moment(p.fecha_hasta_fmt || p.fecha_hasta, 'DD/MM/YYYY')
        if (h && h.isValid()) return !(h.isBefore(pd) || d.isAfter(ph))
        return !(d.isAfter(ph) || d.isBefore(pd))
      })
      return !solapa
    },
    async guardarPrecio() {
      if (!this.data.modelo || this.data.precio == null) {
        this.$store.dispatch('show_snackbar', { color: 'warning', text: 'Complete modelo y precio.' })
        return
      }
      if (!this.validarSolapamiento()) {
        this.$store.dispatch('show_snackbar', { color: 'warning', text: 'El período se solapa con uno existente.' })
        return
      }
      this.$store.state.loading = true
      const payload = {
        modelo: this.data.modelo,
        precio: Number(this.data.precio),
        desde: this.data.desde,
        hasta: this.data.hasta || this.data.desde
      }
      const res = await this.$store.dispatch('preciosModelosV2/postPreciosModelos', payload)
      this.$store.state.loading = false
      if (res.exito === 1) {
        this.$emit('precioGuardado', { desde: this.data.desde, hasta: this.data.hasta })
        this.dialog = false
        if (this.$swal) this.$swal.fire({ icon: 'success', title: res.message })
        else this.$store.dispatch('show_snackbar', { color: 'success', text: res.message })
      } else {
        this.$store.dispatch('show_snackbar', { color: 'error', text: res.message || 'Error al guardar' })
      }
    }
  }
}
</script>
